/* =====================================================================
 * AgentContent — 에이전트 채팅 콘텐츠 영역
 *
 * 빈 상태(EmptyState) + 메시지 목록 + 파일 프리뷰 + 스크롤 관리
 * TopBar, MessageInput, DnD, Modals 등 공용 셸은 WorkspaceShell이 담당.
 * ===================================================================== */

import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy, useLayoutEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  Sun,
  Code2,
  FileSearch,
  TrendingUp,
  MessageSquare,
  LayoutList,
  HardDrive,
  BookOpen,
  BarChart3,
  ImagePlus,
  Mail,
  type LucideIcon,
} from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { useConversationRuntime } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import { useChatFeature } from './ChatModeContext';
import { MessageBubble, type ReplyToInfo } from './bubble/MessageBubble';
import { useScrollToHashMessage } from './useScrollToHashMessage';
import type { BrowserLiveViewPayload } from '@auw/workspace-utils';
import {
  isConversationLiveViewAvailable,
  normalizeConversationLiveView,
} from '@auw/workspace-utils';
import { MessageSkeleton, FilePreviewSkeleton, SkeletonColumn } from '@auw/workspace-ui';
import { EmptyState } from '@auw/workspace-ui';
import { emitAppEvent, onAppEvent } from '@auw/stores';
import { markFirstMessagePaint } from '@auw/workspace-utils';
import { useTranslation } from 'react-i18next';
import { resolveAgentEmptyState, useTenant } from '@auw/tenant';

const FilePreviewPanel = lazy(() =>
  import('@auw/workspace-ui').then((m) => ({ default: m.FilePreviewPanel })),
);
import { LiveViewPanel } from './LiveViewPanel';

/* ------------------------------------------------------------------ */
/* Empty-state defaults (used when manifest doesn't override)          */
/* ------------------------------------------------------------------ */

/** B2C-equivalent default for the rotating suffix carousel. Tenants that
 *  ship a single product mark (e.g. Samsung "SOLA") override this in
 *  `manifest.chat.agentEmptyState.rotatingSuffixes` with a single-entry
 *  array — `EmptyState` then renders the suffix statically without
 *  animation. */
const DEFAULT_AGENT_BRAND_SUFFIXES = [
  '.team',
  '.agent',
  '.mate',
  '.rebo',
];

const DEFAULT_AGENT_TITLE_PREFIX = 'Askyour';

/** Opaque suggestion icon ids exposed via the manifest. The mapping
 *  lives in this consumer (not in `@auw/tenant`) because the icon
 *  catalogue is a UI choice — tenants ship a string, this component
 *  picks a glyph. Adding a new id here is purely additive; unknown ids
 *  fall through to `Sun` so manifests stay forward-compatible.  */
const SUGGESTION_ICON_MAP: Record<string, LucideIcon> = {
  sun: Sun,
  code: Code2,
  fileSearch: FileSearch,
  trending: TrendingUp,
  message: MessageSquare,
  list: LayoutList,
  drive: HardDrive,
  book: BookOpen,
  chart: BarChart3,
  image: ImagePlus,
  mail: Mail,
};

function pickSuggestionIcon(iconKey: string | undefined): LucideIcon {
  if (!iconKey) return Sun;
  return SUGGESTION_ICON_MAP[iconKey] ?? Sun;
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  position: relative;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const MessagesInner = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

/* ---- Suggestion Carousel ---- */

const suggestionEnter = keyframes`
  0%   { opacity: 0; transform: translateY(16px) scale(0.97); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const EmptyStateActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const SuggestionCarousel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-top: 0px;
  min-height: 80px;
`;

const SuggestionCard = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 24px;
  border-radius: 16px;
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.5;
  cursor: pointer;
  animation: ${suggestionEnter} 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  /* 한 줄 표시를 위해 카드 폭을 넉넉히 둔다(아이콘+배지 여백 포함).
   * 좁은 화면에서는 92vw로 제한되고 텍스트는 말줄임된다. */
  max-width: min(640px, 92vw);
  width: 100%;
  text-align: left;

  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(28, 28, 30, 0.46)' : 'rgba(255, 255, 255, 0.58)'};
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 2px 12px rgba(0,0,0,0.25)'
      : '0 2px 12px rgba(0,0,0,0.05)'};

  /* Hover ring uses the tenant brand gradient. Both stops come from
   * the design-system palette (theme.colors.brand /
   * theme.colors.brandGradientEnd) so the empty-state ribbon adopts
   * the manifest's theme.brand override automatically — no
   * per-tenant CSS branch. B2C (orange #f97316) and Samsung (#0055FF)
   * light up with their own colour. */
  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.brand},
      ${({ theme }) => theme.colors.brandGradientEnd}
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: transparent;
    /* brandSoft is pre-baked as rgba(theme.colors.brand, 0.10) in
     * the palette, so it works as a tinted lift-shadow at any brand
     * colour without per-call alpha math. */
    box-shadow: 0 8px 28px ${({ theme }) => theme.colors.brandSoft};

    &::before { opacity: 1; }
  }

  &:active { transform: translateY(0) scale(0.98); }
`;

const SuggestionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 22%;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, #434345, #38383a)'
      : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.35)'
      : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e')};
  flex-shrink: 0;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  ${SuggestionCard}:hover & {
    transform: scale(1.12) rotate(-6deg);
    /* Glyph + halo also derive from the tenant brand colour. The
     * dark-mode halo uses the same brandSoft token; design-system
     * tunes softBg per-mode so the contrast stays acceptable on
     * both surfaces without a manual rgba override here. */
    color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.brandSoft};
    box-shadow: 0 2px 8px ${({ theme }) => theme.colors.brandSoft};
  }
`;

const SuggestionText = styled.span`
  flex: 1;
  /* 예시 카드는 항상 한 줄로 표시한다. 카드 폭(아래 max-width) 안에서
   * 모든 기본 프롬프트가 한 줄에 들어가며, 그보다 긴 문구만 말줄임 처리. */
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SuggestionBadge = styled.span`
  margin-left: auto;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  letter-spacing: 0.01em;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textMuted};
  transition: all 0.3s ease;
  white-space: nowrap;

  ${SuggestionCard}:hover & {
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.brand},
      ${({ theme }) => theme.colors.brandGradientEnd}
    );
    color: ${({ theme }) => theme.colors.textInverse};
    box-shadow: 0 2px 8px ${({ theme }) => theme.colors.brandSoft};
  }
`;

const progressFill = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

const ProgressBars = styled.div`
  display: flex;
  gap: 4px;
  width: 160px;
`;

const ProgressBarTrack = styled.div`
  flex: 1;
  height: 3px;
  border-radius: 1.5px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  overflow: hidden;
  cursor: pointer;
  transition: opacity 0.2s ease;
  &:hover { opacity: 0.7; }
`;

const ProgressBarFill = styled.div<{ $state: 'done' | 'active' | 'upcoming' }>`
  height: 100%;
  border-radius: 1.5px;
  background: ${({ theme }) => theme.colors.textSecondary};
  transform-origin: left;
  transform: scaleX(0);

  ${({ $state }) =>
    $state === 'done' &&
    css`transform: scaleX(1);`}

  ${({ $state }) =>
    $state === 'active' &&
    css`animation: ${progressFill} ${SUGGESTION_INTERVAL}ms linear forwards;`}
`;

/* ---- File preview split layout ---- */

/** 좁은 콘텐츠 영역 기준 (실제 채팅 영역 너비 기준, 사이드바 제외) */
const NARROW_CONTENT_WIDTH = 1280;
const NEAR_BOTTOM_PX = 120;
const USER_ANCHOR_TOP_PX = 12;
/** 앵커링 활성 시 메시지 영역 끝에 추가하는 빈 spacer 높이.
 *
 *  반드시 컨테이너 viewport 1개분(`100vh`) 이상이어야 한다.  spacer 가
 *  부족하면 `desired_top = userRow.top - 12` 가 컨테이너의 `max scrollTop`
 *  (= scrollHeight - clientHeight) 을 초과해 브라우저가 자동 clamp 하고,
 *  그 결과 user 버블이 viewport 상단까지 올라가지 못한 채 "절반만 점프"
 *  하는 현상이 발생한다.  특히 라이브 에이전트는 partial transcript 첫
 *  chunk 가 1-3글자로 매우 짧게 도착하므로 user bubble height 가 짧아
 *  이 clamp 가 더 자주 발생한다 — 그래서 통합 에이전트와 1:1 동일한
 *  정책으로 100vh 를 사용한다. */
const USER_ANCHOR_BOTTOM_SPACER = '100vh';
const PROGRAMMATIC_SCROLL_GUARD_MS = 120;

function nowMs() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

const BodyRow = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  min-width: 0;
`;

/**
 * FilePreviewPanel 래퍼
 * - 와이드 (>= 1280px): 60% 너비, Panel 자체는 preview-embedded 클래스로 100% 채움
 * - 좁은 PC (< 1280px, 프리뷰 표시): flex: 1 (채팅 숨김 시 전체)
 * - 좁은 PC (< 1280px, 채팅 표시): display: none
 */
/**
 * FilePreviewPanel 래퍼
 * - 와이드: 60% 너비, preview-embedded 클래스로 Panel은 100% 채움
 * - 좁음($narrowHidden=false): flex: 1 (전체 너비)
 * - 좁음($narrowHidden=true): display: none
 * isNarrow는 JS(ResizeObserver)로 제어하므로 CSS 미디어쿼리 불필요
 */
const PreviewWrapper = styled.div<{ $isNarrow?: boolean; $narrowHidden?: boolean; $fullscreen?: boolean }>`
  position: relative;
  display: ${({ $narrowHidden }) => ($narrowHidden ? 'none' : 'flex')};
  flex-direction: column;
  flex-shrink: 0;
  /* preview-embedded 클래스로 Panel margin이 0이 되므로, 외부 여백을 여기서 처리 */
  padding: 10px 0 16px 10px;

  ${({ $fullscreen }) =>
    $fullscreen &&
    css`
      flex: 1;
      width: auto;
      padding: 10px;
      overflow: hidden;
      min-width: 0;
    `}

  ${({ $isNarrow, $fullscreen }) =>
    !$fullscreen &&
    ($isNarrow
      ? css`
          flex: 1;
          width: auto;
          padding: 10px;
          overflow: hidden;
          min-width: 0;
        `
      : css`
          width: 60%;
        `)}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

const PreviewFallbackCard = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.03),
    0 4px 16px rgba(0, 0, 0, 0.06),
    0 12px 40px rgba(0, 0, 0, 0.04);
`;

const PreviewFallbackHeader = styled.div`
  flex-shrink: 0;
  height: 48px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const PreviewFallbackContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const PreviewPanelFallback: React.FC = () => (
  <PreviewFallbackCard>
    <PreviewFallbackHeader />
    <PreviewFallbackContent>
      <FilePreviewSkeleton />
    </PreviewFallbackContent>
  </PreviewFallbackCard>
);


const ChatColumn = styled.div<{ $hasPreview: boolean; $hidden?: boolean; $narrowHidden?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  min-width: 0;

  ${({ $hidden }) => $hidden && css`display: none;`}
  ${({ $narrowHidden }) => $narrowHidden && css`display: none;`}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    ${({ $hasPreview }) => $hasPreview && css`display: none;`}
  }
`;

/* ------------------------------------------------------------------ */
/* Suggestions data                                                    */
/* ------------------------------------------------------------------ */

const AGENT_SUGGESTION_KEYS = [
  { textKey: 'chat.examplePrompt1', icon: Sun, badgeKey: 'chat.exampleBadge1' },
  { textKey: 'chat.examplePrompt2', icon: Code2, badgeKey: 'chat.exampleBadge2' },
  { textKey: 'chat.examplePrompt3', icon: BarChart3, badgeKey: 'chat.exampleBadge3' },
  { textKey: 'chat.examplePrompt4', icon: TrendingUp, badgeKey: 'chat.exampleBadge4' },
  { textKey: 'chat.examplePrompt5', icon: ImagePlus, badgeKey: 'chat.exampleBadge5' },
  { textKey: 'chat.examplePrompt6', icon: Mail, badgeKey: 'chat.exampleBadge6' },
];

const SUGGESTION_INTERVAL = 3500;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface AgentContentProps {
  emptyDescription?: string;
}

export const AgentContent: React.FC<AgentContentProps> = ({
  emptyDescription,
}) => {
  const { t } = useTranslation('common');
  const tenant = useTenant();
  /**
   * Empty-state copy resolution chain (highest priority first):
   *   1. `manifest.chat.agentEmptyState.*` — tenant-specific copy
   *      (Samsung's SOLA product mark, on-prem brand, …).
   *   2. Component prop `emptyDescription` — kept as a legacy escape
   *      hatch for callers that already inject a description (e.g.
   *      live-agent's modal which renders this same component with
   *      its own welcome line).
   *   3. Bundled i18n default (`chat.welcomeMessage`) + module-scope
   *      `DEFAULT_AGENT_*` constants — preserves byte-for-byte parity
   *      with the pre-Phase-12 hard-coded "Askyour" carousel.
   * The resolver only handles slot-level fallback; we still hand it
   * the materialised i18n string so `@auw/tenant` does not have to
   * depend on `react-i18next`.
   */
  const emptyState = useMemo(
    () =>
      resolveAgentEmptyState(tenant, {
        titlePrefix: DEFAULT_AGENT_TITLE_PREFIX,
        rotatingSuffixes: DEFAULT_AGENT_BRAND_SUFFIXES,
        description: emptyDescription ?? t('chat.welcomeMessage'),
      }),
    [tenant, emptyDescription, t],
  );
  const messages = useChatStore((s) => s.activeMessages());
  const sendMessage = useChatStore((s) => s.sendMessage);
  const loadingConversationId = useChatStore((s) => s.loadingConversationId);
  const previewFile = useChatStore((s) => s.previewFile);
  const setPreviewFile = useChatStore((s) => s.setPreviewFile);
  const previewFullscreen = useChatStore((s) => s.previewFullscreen);
  const togglePreviewFullscreen = useChatStore((s) => s.togglePreviewFullscreen);
  const applyConversationLiveViewEvent = useChatStore((s) => s.applyConversationLiveViewEvent);
  const setConversationLiveViewOpen = useChatStore((s) => s.setConversationLiveViewOpen);
  const activeConv = useChatStore((s) => s.activeConversation());
  const conversationId = activeConv?.id ?? null;
  const { runtime, isStreaming: streaming } = useConversationRuntime(conversationId);
  const toggleReaction = useChatStore((s) => s.toggleReaction);
  const regenerateMessage = useChatStore((s) => s.regenerateMessage);
  const liveViewRuntime = runtime?.liveView ?? null;
  const currentUserId = useUserStore((s) => s.user?.userId || s.user?.email || '');
  const currentUserName = useUserStore((s) => s.user?.name || s.user?.email || '');
  const currentUserPicture = useUserStore((s) => s.user?.picture ?? null);
  const sharedGroup = activeConv?.sharedGroup ?? null;
  const resolvedConversationId = conversationId ?? '';
  const conversationLoading = !!resolvedConversationId && loadingConversationId === resolvedConversationId;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [isAnchoringUser, setIsAnchoringUser] = useState(false);
  const anchorActiveRef = useRef(false);
  const pendingAnchorRequestRef = useRef(false);
  const pendingAnchorBaselineUserIdRef = useRef<string | null>(null);
  const anchorMessageIdRef = useRef<string | null>(null);
  const lastProgrammaticScrollAtRef = useRef(0);
  const initialScrollConversationRef = useRef('');
  const lastMessage = messages[messages.length - 1] ?? null;

  const isEmpty = messages.length === 0;
  /* 파일 저장소의 "출처" 바로가기로 들어온 #m-{messageId} 해시를 처리해
   * 메시지가 DOM 에 마운트되는 즉시 해당 버블로 부드럽게 스크롤한다.
   * agent 모드(/agents/{itemId})는 ChatArea 가 아닌 AgentContent 가
   * 실제 채팅 영역을 렌더하므로 여기에서 훅을 호출해야 한다. */
  useScrollToHashMessage(containerRef, !isEmpty);
  const liveViewWorkspaceType = activeConv?.mode ?? 'agent';
  const effectiveLiveView = useMemo(() => {
    const persisted = activeConv?.liveView ?? null;
    const hasRuntime = !!liveViewRuntime?.liveViewRef;
    const hasPersisted = !!persisted?.liveViewRef;
    if (!hasRuntime && !hasPersisted) return null;
    return {
      liveViewRef: liveViewRuntime?.liveViewRef ?? persisted?.liveViewRef ?? null,
      available: liveViewRuntime?.available ?? persisted?.available ?? false,
      watchOnly: liveViewRuntime?.watchOnly ?? persisted?.watchOnly ?? true,
      state: liveViewRuntime?.state ?? persisted?.state ?? null,
      errorMessage: liveViewRuntime?.errorMessage ?? persisted?.errorMessage ?? null,
      open: liveViewRuntime?.open ?? false,
      running: liveViewRuntime?.running ?? false,
    };
  }, [activeConv?.liveView, liveViewRuntime]);
  const liveViewAvailable = useMemo(
    () =>
      isConversationLiveViewAvailable(
        normalizeConversationLiveView({
          liveViewRef: effectiveLiveView?.liveViewRef ?? null,
          state: effectiveLiveView?.state ?? 'closed',
          available: effectiveLiveView?.available ?? false,
          watchOnly: effectiveLiveView?.watchOnly ?? true,
          errorMessage: effectiveLiveView?.errorMessage ?? null,
        }),
      ),
    [effectiveLiveView],
  );
  /* Phase 11-5: tenant gate for the browser live-view panel.  When
   * `mode.agent.liveView` is disabled (e.g. Samsung on-prem which bans
   * browser automation surfaces for compliance), the right-hand panel
   * never mounts even if the backend emits `liveView.open=true`.  The
   * corresponding `workspace.liveView` workspace-shell flag mirrors
   * this at the WorkspaceShell level for non-agent modes. */
  // PR-FE-1: was ``useFeature('mode.agent.liveView')`` — namespaced
  // via ChatModeContext so doc-agent / other feature mounts get
  // their own ``mode.<X>.liveView`` flag without forking AgentContent.
  const agentLiveViewEnabled = useChatFeature('liveView');
  const showLiveViewPanel = !!(
    agentLiveViewEnabled &&
    !previewFile &&
    conversationId &&
    effectiveLiveView?.open &&
    effectiveLiveView.liveViewRef &&
    liveViewAvailable
  );
  const hasSidePanel = !!previewFile || showLiveViewPanel;
  const hasPreview = !!previewFile;
  const isSidePanelFullscreen = !!previewFile && previewFullscreen;

  const setNarrowLayout = useChatStore((s) => s.setNarrowLayout);

  const handleCloseLiveView = useCallback(() => {
    if (!conversationId) return;
    setConversationLiveViewOpen(conversationId, false);
  }, [conversationId, setConversationLiveViewOpen]);

  const handleLiveViewSessionClosed = useCallback(
    (errorMessage?: string | null) => {
      if (!conversationId || !effectiveLiveView?.liveViewRef) return;
      const closedPayload: BrowserLiveViewPayload = {
        kind: 'browser_live_view',
        state: 'closed',
        live_view_ref: effectiveLiveView.liveViewRef,
        available: false,
        watch_only: effectiveLiveView.watchOnly,
        item_id: conversationId,
        workspace_type: liveViewWorkspaceType,
        updated_at: new Date().toISOString(),
        ...(errorMessage ? { error_message: errorMessage } : {}),
      };
      applyConversationLiveViewEvent(conversationId, closedPayload, {
        autoOpen: false,
        running: false,
      });
    },
    [
      applyConversationLiveViewEvent,
      conversationId,
      effectiveLiveView?.liveViewRef,
      effectiveLiveView?.watchOnly,
      liveViewWorkspaceType,
    ],
  );

  /* ---- 실제 콘텐츠 영역 너비 기준 narrow 감지 (사이드바 제외) ---- */
  const bodyRowRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const el = bodyRowRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const narrow = entry.contentRect.width < NARROW_CONTENT_WIDTH;
      setIsNarrow(narrow);
      setNarrowLayout(narrow);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [setNarrowLayout]);

  const narrowPreviewView = useChatStore((s) => s.narrowPreviewView);
  const setNarrowPreviewView = useChatStore((s) => s.setNarrowPreviewView);

  // 새 메시지 전송 시 → 채팅 영역으로 전환
  const prevMessagesLength = useRef(messages.length);
  useEffect(() => {
    if (messages.length > prevMessagesLength.current && isNarrow && hasSidePanel) {
      setNarrowPreviewView('chat');
    }
    prevMessagesLength.current = messages.length;
  }, [messages.length, isNarrow, hasSidePanel, setNarrowPreviewView]);

  const isNarrowPreviewHidden = isNarrow && hasSidePanel && narrowPreviewView === 'chat';
  const isNarrowContentHidden = isNarrow && hasSidePanel && narrowPreviewView === 'preview';

  /* ---- Suggestion carousel ----
   * Source priority:
   *   1. `manifest.chat.agentEmptyState.suggestions` — tenant-curated
   *      list. An empty array is honoured (carousel hides itself).
   *   2. Bundled i18n default (`chat.examplePrompt1..8` + matching
   *      `chat.exampleBadge1..8`), preserving the exact carousel B2C
   *      has shipped since launch. The icon stays mapped via
   *      `AGENT_SUGGESTION_KEYS` so the visual rhythm stays intact.
   * The `Icon` component is resolved here (not in the manifest) via
   * `pickSuggestionIcon(iconKey)` so manifests carry only opaque
   * string ids.
   */
  const suggestions = useMemo(() => {
    if (emptyState.suggestions) {
      return emptyState.suggestions.map((entry) => ({
        text: entry.text,
        badge: entry.badge ?? '',
        icon: pickSuggestionIcon(entry.iconKey),
      }));
    }
    return AGENT_SUGGESTION_KEYS.map((s) => ({
      text: t(s.textKey),
      icon: s.icon,
      badge: t(s.badgeKey),
    }));
  }, [emptyState.suggestions, t]);
  const hasSuggestions = suggestions.length > 0;
  const [activeIdx, setActiveIdx] = useState(0);

  /* Reset to the first slide whenever the suggestion list itself
   * changes (e.g. tenant runtime-config overlay swaps the curated
   * list, or B2C i18n reloads). Without this, `activeIdx` could
   * point past `suggestions.length - 1` after the swap, rendering
   * `undefined` for one frame. */
  useEffect(() => {
    setActiveIdx(0);
  }, [suggestions]);

  const latestUserMessageId = useMemo(() => {
    for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
      if (messages[idx].role === 'user') {
        return messages[idx].id;
      }
    }
    return null;
  }, [messages]);

  const replyToByMessageId = useMemo(() => {
    const replyMap = new Map<string, ReplyToInfo>();
    if (!sharedGroup) return replyMap;

    const memberMap = new Map(sharedGroup.members.map((m) => [m.user_id, m]));
    const selfDisplayName = currentUserName.trim() || t('chat.me');
    let lastUserMessage: (typeof messages)[number] | null = null;
    let lastUserIndex = -1;

    for (let idx = 0; idx < messages.length; idx += 1) {
      const msg = messages[idx];
      if (msg.role === 'user') {
        lastUserMessage = msg;
        lastUserIndex = idx;
        continue;
      }
      if (msg.role !== 'assistant' || !lastUserMessage) continue;

      const isMe = !!lastUserMessage.senderId && lastUserMessage.senderId === currentUserId;
      const member = lastUserMessage.senderId
        ? memberMap.get(lastUserMessage.senderId)
        : null;
      const isImmediatePrev = lastUserIndex === idx - 1;
      const likelyMineWithoutSender = isImmediatePrev && !lastUserMessage.senderId && !lastUserMessage.senderName;
      const resolvedSenderName = isMe
        ? selfDisplayName
        : (lastUserMessage.senderName
            || member?.name
            || member?.email
            || (likelyMineWithoutSender ? selfDisplayName : t('chat.teamMember')));

      replyMap.set(msg.id, {
        messageId: lastUserMessage.id,
        senderName: resolvedSenderName,
        senderPicture: isMe
          ? currentUserPicture
          : (member?.picture ?? (likelyMineWithoutSender ? currentUserPicture : null)),
        content: lastUserMessage.content,
      });
    }
    return replyMap;
  }, [messages, sharedGroup, currentUserName, currentUserId, currentUserPicture]);

  const syncNearBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setIsNearBottom(el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX);
  }, []);

  const scrollToBottom = useCallback((behavior: 'auto' | 'smooth' = 'smooth') => {
    const el = containerRef.current;
    if (!el) return;
    lastProgrammaticScrollAtRef.current = nowMs();
    el.scrollTo({ top: el.scrollHeight, behavior });
    setIsNearBottom(true);
  }, []);

  const alignAnchoredUser = useCallback(() => {
    const el = containerRef.current;
    const anchorMessageId = anchorMessageIdRef.current;
    if (!el || !anchorMessageId) return false;

    const anchorEl = Array.from(el.querySelectorAll<HTMLElement>('[data-message-id]'))
      .find((node) => node.dataset.messageId === anchorMessageId);
    if (!anchorEl) return false;

    const nextTop = el.scrollTop
      + (anchorEl.getBoundingClientRect().top - el.getBoundingClientRect().top)
      - USER_ANCHOR_TOP_PX;

    lastProgrammaticScrollAtRef.current = nowMs();
    el.scrollTo({ top: Math.max(nextTop, 0), behavior: 'auto' });
    return true;
  }, []);

  const requestUserAnchor = useCallback(() => {
    anchorActiveRef.current = true;
    pendingAnchorRequestRef.current = true;
    pendingAnchorBaselineUserIdRef.current = latestUserMessageId;
    setIsAnchoringUser(true);
  }, [latestUserMessageId]);

  const releaseAnchor = useCallback(() => {
    anchorActiveRef.current = false;
    pendingAnchorRequestRef.current = false;
    pendingAnchorBaselineUserIdRef.current = null;
    anchorMessageIdRef.current = null;
    setIsAnchoringUser(false);
  }, []);

  const anchorLayoutSignature = [
    messages.length,
    lastMessage?.id ?? 'empty',
    lastMessage?.content.length ?? 0,
    lastMessage?.thinkingContent?.length ?? 0,
    lastMessage?.streamEvents.length ?? 0,
  ].join(':');

  const scrollToMessage = useCallback((messageId: string) => {
    const el = containerRef.current;
    if (!el) return;
    const target = Array.from(el.querySelectorAll<HTMLElement>('[data-message-id]'))
      .find((node) => node.dataset.messageId === messageId);
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  useLayoutEffect(() => {
    if (!isAnchoringUser) return;
    if (pendingAnchorRequestRef.current) {
      if (!latestUserMessageId) return;
      if (latestUserMessageId === pendingAnchorBaselineUserIdRef.current) return;
      anchorMessageIdRef.current = latestUserMessageId;
      pendingAnchorRequestRef.current = false;
      pendingAnchorBaselineUserIdRef.current = null;
    }
    if (!anchorMessageIdRef.current) return;
    alignAnchoredUser();
    syncNearBottom();
  }, [alignAnchoredUser, anchorLayoutSignature, isAnchoringUser, latestUserMessageId, syncNearBottom]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    let rafId = 0;
    const scheduleRealign = () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      rafId = window.requestAnimationFrame(() => {
        if (anchorActiveRef.current) {
          alignAnchoredUser();
        } else {
          syncNearBottom();
        }
      });
    };

    const observer = new ResizeObserver(scheduleRealign);
    observer.observe(el);
    const content = el.firstElementChild;
    if (content instanceof Element) {
      observer.observe(content);
    }

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      observer.disconnect();
    };
  }, [alignAnchoredUser, resolvedConversationId, syncNearBottom, messages.length]);

  useEffect(() => {
    releaseAnchor();
    initialScrollConversationRef.current = '';
    setIsNearBottom(true);
  }, [releaseAnchor, resolvedConversationId]);

  useEffect(() => {
    if (!resolvedConversationId || conversationLoading) return;
    if (messages.length === 0) return;
    if (initialScrollConversationRef.current === resolvedConversationId) return;
    initialScrollConversationRef.current = resolvedConversationId;
    const rafId = window.requestAnimationFrame(() => {
      scrollToBottom('auto');
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [resolvedConversationId, conversationLoading, messages.length, scrollToBottom]);

  useEffect(() => {
    if (!isEmpty || !hasSuggestions) return;
    /* Single-entry tenants (e.g. Samsung's curated 4 prompts trimmed
     * to one) still benefit from the progress-bar fill animation,
     * but rotation past length-of-1 would loop on a no-op — skip the
     * interval entirely in that case to avoid useless React renders. */
    if (suggestions.length === 1) return;
    const timer = setInterval(() => {
      setActiveIdx((prev) => (prev + 1) % suggestions.length);
    }, SUGGESTION_INTERVAL);
    return () => clearInterval(timer);
  }, [isEmpty, hasSuggestions, suggestions.length]);

  useEffect(() => {
    if (!resolvedConversationId || conversationLoading) return;
    if (messages.length === 0) return;
    markFirstMessagePaint(resolvedConversationId, messages.length);
  }, [resolvedConversationId, conversationLoading, messages.length]);

  /* 전송 시 최신 유저 버블을 상단 앵커 위치로 고정 */
  useEffect(() => {
    return onAppEvent('agent:anchorLatestUser', requestUserAnchor);
  }, [requestUserAnchor]);

  /* ScrollToBottom 버튼은 기존처럼 실제 맨 아래로 이동 */
  useEffect(() => {
    return onAppEvent('agent:scrollToBottom', () => {
      releaseAnchor();
      scrollToBottom('smooth');
    });
  }, [releaseAnchor, scrollToBottom]);

  const handleScroll = useCallback(() => {
    syncNearBottom();
    if (!anchorActiveRef.current) return;
    if (nowMs() - lastProgrammaticScrollAtRef.current < PROGRAMMATIC_SCROLL_GUARD_MS) return;
    releaseAnchor();
  }, [releaseAnchor, syncNearBottom]);

  /* 스크롤 상태를 WorkspaceShell에 알려줌 (ScrollToBottom 버튼용) */
  useEffect(() => {
    emitAppEvent('agent:scrollState', { isEmpty, isNearBottom, streaming });
  }, [isEmpty, isNearBottom, streaming]);

  return (
    <BodyRow ref={bodyRowRef}>
      {hasSidePanel && (
        <PreviewWrapper
          className="preview-embedded"
          $isNarrow={isNarrow}
          $narrowHidden={isNarrowPreviewHidden}
          $fullscreen={isSidePanelFullscreen}
        >
          {previewFile ? (
            <Suspense fallback={<PreviewPanelFallback />}>
              <FilePreviewPanel
                key={previewFile.id}
                file={previewFile}
                onClose={() => setPreviewFile(null)}
                fullscreen={previewFullscreen}
                onToggleFullscreen={togglePreviewFullscreen}
              />
            </Suspense>
          ) : (
            showLiveViewPanel &&
            conversationId &&
            effectiveLiveView?.liveViewRef && (
              <LiveViewPanel
                key={effectiveLiveView.liveViewRef}
                liveViewRef={effectiveLiveView.liveViewRef}
                conversationId={conversationId}
                workspaceType={liveViewWorkspaceType}
                watchOnly={effectiveLiveView.watchOnly !== false}
                onClose={handleCloseLiveView}
                onSessionClosed={handleLiveViewSessionClosed}
              />
            )
          )}
        </PreviewWrapper>
      )}
      <ChatColumn
        $hasPreview={hasSidePanel}
        $hidden={isSidePanelFullscreen}
        $narrowHidden={isNarrowContentHidden}
      >
        {conversationLoading ? (
          <MessagesContainer>
            <SkeletonColumn>
              <MessageSkeleton />
              <MessageSkeleton />
              <MessageSkeleton />
            </SkeletonColumn>
          </MessagesContainer>
        ) : isEmpty && !hasSidePanel ? (
          <EmptyState
            titleSize="2.25rem"
            titlePrefix={emptyState.titlePrefix}
            rotatingTitles={[...emptyState.rotatingSuffixes]}
            description={emptyState.description}
            descriptionTone="strong"
            action={
              hasSuggestions ? (
                <EmptyStateActions>
                  <SuggestionCarousel>
                    <SuggestionCard
                      key={activeIdx}
                      onClick={() => {
                        emitAppEvent('agent:anchorLatestUser');
                        sendMessage(
                          suggestions[activeIdx].text,
                          undefined,
                          { modeOverride: activeConv?.mode ?? 'agent' },
                        );
                      }}
                    >
                      <SuggestionIcon>
                        {React.createElement(suggestions[activeIdx].icon, { size: 18 })}
                      </SuggestionIcon>
                      <SuggestionText>{suggestions[activeIdx].text}</SuggestionText>
                      {suggestions[activeIdx].badge && (
                        <SuggestionBadge>{suggestions[activeIdx].badge}</SuggestionBadge>
                      )}
                    </SuggestionCard>
                    {suggestions.length > 1 && (
                      <ProgressBars>
                        {suggestions.map((_, i) => {
                          const state = i < activeIdx ? 'done' : i === activeIdx ? 'active' : 'upcoming';
                          return (
                            <ProgressBarTrack key={i} onClick={() => setActiveIdx(i)}>
                              <ProgressBarFill key={`fill-${i}-${activeIdx}`} $state={state} />
                            </ProgressBarTrack>
                          );
                        })}
                      </ProgressBars>
                    )}
                  </SuggestionCarousel>
                </EmptyStateActions>
              ) : null
            }
          />
        ) : (
          <MessagesContainer
            ref={containerRef}
            onScroll={handleScroll}
            onWheel={releaseAnchor}
            onTouchMove={releaseAnchor}
          >
            <MessagesInner>
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  narrowColumn={hasSidePanel}
                  message={msg}
                  sharedGroup={sharedGroup}
                  currentUserId={currentUserId}
                  conversationId={resolvedConversationId}
                  onToggleReaction={toggleReaction}
                  replyTo={replyToByMessageId.get(msg.id) ?? null}
                  onScrollToMessage={scrollToMessage}
                  onRegenerate={
                    msg.role === 'assistant' && !msg.replacedByMessageId && resolvedConversationId
                      ? (messageId, forceMode) => regenerateMessage(resolvedConversationId, messageId, forceMode)
                      : undefined
                  }
                />
              ))}
              {isAnchoringUser && messages.length > 0 && <div style={{ minHeight: USER_ANCHOR_BOTTOM_SPACER }} />}
            </MessagesInner>
          </MessagesContainer>
        )}
      </ChatColumn>
    </BodyRow>
  );
};
